import { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';
import { ContactsTypes, ContactsAPI } from '@frontend/api-contacts';
import { PersonAPI, PersonQueries } from '@frontend/api-person';
import { AvatarCropper, readFileAsDataUrl } from '@frontend/avatar-image-cropper';
import { stateOptions, CountryCodes, countryOptions } from '@frontend/geography';
import { useTranslation } from '@frontend/i18n';
import { usePhotoStore } from '@frontend/photos';
import { useMutation } from '@frontend/react-query-helpers';
import { useIsCompactMode } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import {
  DatePickerField,
  DropdownField,
  FormRow,
  PhoneField,
  TextField,
  useForm,
  Text,
  Button,
  PostalCodeField,
  PostalCodeLocale,
} from '@frontend/design-system';
import { useCustomContactFormSlidePanel } from '../../hooks';
import { datePickerStyle, formStyles, modalStyles } from '../style';

type NewContact = ContactsTypes.NewContactPayload;
type LanguageAPIOption = 'Spanish' | 'English';

const digitsOnly = (str = ''): string => str.replace(/\D/g, '');

const sanitizePhoneNumbers = ({ phone_mobile, phone_home, birthdate, ...rest }: NewContact): NewContact => {
  return {
    ...rest,
    phone_mobile: digitsOnly(phone_mobile),
    phone_home: digitsOnly(phone_home),
    birthdate: birthdate ? dayjs(birthdate).format('YYYY-MM-DD') : '',
  };
};

const patterns = {
  [PostalCodeLocale.CA]: /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i,
  [PostalCodeLocale.US]: /^\d{5}(-\d{4})?$/,
};

const getCountry = (zip?: string, state?: string, mode?: string, selectedCountry?: string): CountryCodes => {
  if (mode === 'edit' && (!!zip || !!state)) {
    const usStateCodes = new Set(stateOptions[CountryCodes.USA].map((s) => s.value));
    const caStateCodes = new Set(stateOptions[CountryCodes.Canada].map((s) => s.value));

    if (!!zip) {
      if (patterns[PostalCodeLocale.CA].test(zip)) return CountryCodes.Canada;
      if (patterns[PostalCodeLocale.US].test(zip)) return CountryCodes.USA;
    }

    if (!!state) {
      if (caStateCodes.has(state)) return CountryCodes.Canada;
      if (usStateCodes.has(state)) return CountryCodes.USA;
    }
  }
  return (selectedCountry as CountryCodes) || CountryCodes.USA;
};

export const CustomContactForm = () => {
  const queryClient = useQueryClient();
  const { selectedLocationIds } = useAppScopeStore();
  const defaultLocationId = selectedLocationIds[0];
  const isCompactMode = useIsCompactMode();
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);
  const { t } = useTranslation('contacts');
  const languageOptionsMap = {
    English: t('English'),
    Spanish: t('Spanish'),
    'No Preference': t('No Preference'),
  } as const;
  const setPhotoRecord = usePhotoStore((state) => state.setPhotoRecord);
  const { setShow, context } = useCustomContactFormSlidePanel();
  const { mode, person, locationId: contextLocationId, onSave, onError } = context ?? {};
  const resolvedLocationId = contextLocationId || defaultLocationId;

  const sanitizeLanguage = (language?: string): LanguageAPIOption | null => {
    if (!language || language === 'No Preference') return null;
    if (language === ('English' satisfies LanguageAPIOption)) return 'English';
    if (language === ('Spanish' satisfies LanguageAPIOption)) return 'Spanish';
    return null;
  };

  const addToPhotoUrlToRecord = (croppedBlob: Blob, personId: string) => {
    readFileAsDataUrl(croppedBlob, (photoUrl) => setPhotoRecord(personId, photoUrl));
  };

  const { mutateAsync: uploadMutation, isLoading: uploadLoading } = PersonQueries.useUploadPersonImage();

  const { mutateAsync, isLoading: creatingContact } = useMutation({
    mutationFn: (contactDetails: NewContact) => {
      if (mode === 'edit' && person) {
        if (croppedBlob) {
          addToPhotoUrlToRecord(croppedBlob, person.PersonID);
          uploadMutation({ personId: person.PersonID, image: croppedBlob });
        }
        return ContactsAPI.updateCustomContact(person.PersonID, contactDetails, contextLocationId);
      } else {
        return ContactsAPI.createCustomContact(contactDetails, contextLocationId);
      }
    },
    onSuccess: (data, variables, context) => {
      const personId = data.data.person_id;
      queryClient.invalidateQueries(['persons-v3']);
      if (croppedBlob) {
        addToPhotoUrlToRecord(croppedBlob, personId);
        uploadMutation({ personId, image: croppedBlob });
      }
      onSave?.(data, variables, context);
    },
    onError,
  });

  const { formProps, getFieldProps, isComplete, validate, seedValues, values } = useForm({
    allowInvalidSubmission: false,
    fields: {
      email: { type: 'email', value: person?.Email },
      birthdate: {
        type: 'datePicker',
        value: person?.Birthdate ? dayjs(person?.Birthdate).format('MM/DD/YYYY') : undefined,
      },
      phone_home: { type: 'phone', value: person?.HomePhone },
      gender: { type: 'dropdown', value: person?.Gender },
      first_name: { type: 'text', value: person?.FirstName, required: true },
      last_name: { type: 'text', value: person?.LastName, required: true },
      preferred_name: { type: 'text', value: person?.PreferredName },
      phone_mobile: { type: 'phone', value: person?.MobilePhone },
      language: { type: 'dropdown', value: person?.Preferences?.Language ?? languageOptionsMap['No Preference'] },
      city: { type: 'text', value: person?.Address?.City },
      state: { type: 'dropdown', value: person?.Address?.State },
      street: { type: 'text', value: person?.Address?.Street },
      postCode: { type: 'text', value: person?.Address?.Zip },
      country: {
        type: 'dropdown',
        value: getCountry(person?.Address?.Zip, person?.Address?.State, mode, person?.Address?.Country),
      },
    },
    onSubmit: async ({ language, ...values }) => {
      if (!values.first_name || !values.last_name) {
        validate();
        return;
      }
      const res = await mutateAsync(sanitizePhoneNumbers(values as NewContact));
      mutatePersonPreferences.mutate({ personId: res.data.person_id, language: sanitizeLanguage(language) });
      if (res.data) setShow({ show: false });
    },
  });

  useEffect(() => {
    seedValues({
      phone_mobile: person?.MobilePhone,
    });
  }, [person, seedValues]);

  const closeIconClick = useCallback(() => {
    setShow({ show: false });
  }, [setShow]);

  const mutatePersonPreferences = useMutation({
    mutationFn: ({ personId, language }: { personId: string; language: LanguageAPIOption | null }) =>
      PersonAPI.updatePersonPreferences(
        personId,
        {
          Language: language,
        },
        contextLocationId
      ),
    onSuccess: (_res, req) => queryClient.invalidateQueries({ queryKey: [resolvedLocationId, req.personId] }),
  });

  return (
    <div css={modalStyles}>
      <SurfaceHeader.Root>
        <SurfaceHeader.Row>
          {isCompactMode && (
            <SurfaceHeader.Start>
              <SurfaceHeader.Icon iconName='caret-left-small' onClick={closeIconClick} />
            </SurfaceHeader.Start>
          )}
          <SurfaceHeader.Title>{mode === 'edit' ? t('Update Contact') : t('Create New Contact')}</SurfaceHeader.Title>
          {!isCompactMode && (
            <SurfaceHeader.Actions>
              <SurfaceHeader.Icon iconName='x' label='Close' className='close-btn' onClick={closeIconClick} />
            </SurfaceHeader.Actions>
          )}
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>

      <div
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          overflow-y: auto;
        `}
      >
        <section css={{ width: '100%', marginTop: theme.spacing(1), display: 'flex', justifyContent: 'center' }}>
          <AvatarCropper
            PersonID={person?.PersonID || ''}
            FirstName={person?.FirstName || ''}
            LastName={person?.LastName || ''}
            onCropComplete={setCroppedBlob}
            loading={uploadLoading}
          />
        </section>
        <form css={formStyles} {...formProps} autoComplete='off'>
          <section style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
            <Text size='medium'>{t('General Information')}</Text>
            <FormRow>
              <TextField {...getFieldProps('first_name')} label={t('first name')} />
            </FormRow>
            <FormRow>
              <TextField {...getFieldProps('last_name')} label={t('last name')} />
            </FormRow>
            <FormRow>
              <TextField {...getFieldProps('preferred_name')} label={t('Preferred Name')} />
            </FormRow>
            <FormRow>
              <DropdownField {...getFieldProps('gender')} label={t('gender')}>
                <DropdownField.Option value='Male'>{t('Male')}</DropdownField.Option>
                <DropdownField.Option value='Female'>{t('Female')}</DropdownField.Option>
                <DropdownField.Option value='Nonbinary'>{t('Nonbinary')}</DropdownField.Option>
                <DropdownField.Option value='Other'>{t('Other')}</DropdownField.Option>
                <DropdownField.Option value='Unknown'>{t('Unknown')}</DropdownField.Option>
              </DropdownField>
            </FormRow>
            <FormRow>
              <DatePickerField css={datePickerStyle} {...getFieldProps('birthdate')} label={t('birthdate')} />
            </FormRow>
            <FormRow>
              <DropdownField {...getFieldProps('language')} label={t('Select Language Preference')}>
                {Object.entries(languageOptionsMap).map(([value, translation]) => (
                  <DropdownField.Option key={value} value={value} trackingId='contact-2.0-tray-tab-history-langprefs'>
                    {translation}
                  </DropdownField.Option>
                ))}
              </DropdownField>
            </FormRow>
          </section>
          <section style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
            <Text size='medium'>{t('Contact Information')}</Text>
            <FormRow>
              <PhoneField {...getFieldProps('phone_mobile')} label={t('mobile phone')} />
            </FormRow>
            <FormRow>
              <PhoneField {...getFieldProps('phone_home')} label={t('home phone')} />
            </FormRow>
            <FormRow>
              <TextField {...getFieldProps('email')} label={t('email')} />
            </FormRow>
          </section>

          <section style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
            <Text size='medium'>{t('Address')}</Text>
            <FormRow>
              <TextField {...getFieldProps('street')} label={t('Street')} />
            </FormRow>
            <FormRow>
              <TextField {...getFieldProps('city')} label={t('City')} />
            </FormRow>
            <FormRow>
              <DropdownField
                {...getFieldProps('country')}
                label={t('Country')}
                onChange={(e) => {
                  getFieldProps('country').onChange?.(e);
                  seedValues({
                    state: '',
                    postCode: '',
                  });
                }}
              >
                {countryOptions.map((country) => (
                  <DropdownField.Option key={country.value} value={country.value}>
                    {country.label}
                  </DropdownField.Option>
                ))}
              </DropdownField>
            </FormRow>
            <FormRow>
              <DropdownField
                {...getFieldProps('state')}
                label={values.country === CountryCodes.Canada ? t('Province') : t('State')}
              >
                <DropdownField.Option value=''>{t('Select State')}</DropdownField.Option>
                {stateOptions[values.country as CountryCodes]?.map((state) => (
                  <DropdownField.Option key={state.value} value={state.value} searchValue={state.label}>
                    {state.label}
                  </DropdownField.Option>
                ))}
              </DropdownField>
            </FormRow>
            <FormRow>
              <PostalCodeField
                {...getFieldProps('postCode')}
                label={values.country === CountryCodes.Canada ? t('Postal Code') : t('Zip Code')}
                locale={values.country as PostalCodeLocale}
              />
            </FormRow>
          </section>

          <div className='form-action'>
            <Button type='submit' disabled={creatingContact || !isComplete}>
              {t('Save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
